import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismIcon, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralSmartInsightsDetailViewSmartInsightCard from 'lumen/components/general/smart-insights/detail-view/smart-insight-card';
import GeneralSmartInsightsDetailViewSmartInsightKpiBlock from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-block';
import GeneralSmartInsightsDetailViewSmartInsightKpiDelta from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-delta';
import type { KpiHoverData } from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-hover-panel';
import type { SmartInsightDay } from 'lumen/models';
import type { SmartInsightDayInsightEntrySource } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import type { MarketDemandLevel } from 'lumen/utils/constants/general-constants';
import { RATE_PARAMS } from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
} from 'lumen/utils/constants/market-insight-constants';
import { OD_SELECTED_TILES_IDS } from 'lumen/utils/constants/overview-dashboard-constants';
import { RATES_VIEWS } from 'lumen/utils/constants/rate-constants';
import {
  SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS,
  SMART_INSIGHTS_KPI_COMPARISON_PERIODS,
  type SmartInsightsKpiComparisonPeriod,
} from 'lumen/utils/constants/smart-insights';
import { getDemandLevel } from 'lumen/utils/demand-level';
import { stringifyObject } from 'lumen/utils/parse-utils';
import { DAY_BY_DAY_PATHS } from 'lumen/utils/spider';

import titleStyles from './smart-insight-kpi-block.module.css';
import styles from './smart-insight-kpi-blocks.module.css';

interface GeneralSmartInsightsDetailViewSmartInsightKpiBlocksSignature {
  Args: {
    openInNewTab?: boolean;
    setSmartInsightsKpiComparisonPeriod: (
      period: SmartInsightsKpiComparisonPeriod,
    ) => void;
    smartInsightDay: SmartInsightDay;
    smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod;
  };
}

type ComparisonPeriodOption = Option & {
  label: string;
  value: SmartInsightsKpiComparisonPeriod;
};

type NumberFormatOptions = NonNullable<
  Parameters<Services['intl']['formatNumber']>[1]
>;

type Nullable = number | null | undefined;

interface PickupChange {
  formatted: string;
  value: number | null;
}

interface RateColumn {
  alignClass: string;
  change: PickupChange;
  iconName?: IconName;
  id: string;
  label: string;
  value: string;
}

export default class GeneralSmartInsightsDetailViewSmartInsightKpiBlocks extends Component<GeneralSmartInsightsDetailViewSmartInsightKpiBlocksSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare spider: Services['spider'];

  get adrChangeValue(): number | undefined {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY:
        return this.kpis.adrPickupVsYesterday;
      default:
        return undefined;
    }
  }

  get adrChangeValueFormatted(): string | undefined {
    const changeValue = this.adrChangeValue;

    return changeValue === undefined
      ? undefined
      : this.formatSignedCurrency(changeValue);
  }

  get adrHover(): KpiHoverData {
    return {
      thisYear: this.kpis.adr,
      stly: this.kpis.adrStly,
      lastYearFinal: this.kpis.adrLastYearFinal,
      // ADR pickup is only provided for yesterday.
      pickupThisYear: this.adrChangeValue,
      pickupStly: this.adrStlyPickup,
      pickupPeriod: this.args.smartInsightsKpiComparisonPeriod,
      format: (value) => this.formatCurrency(value),
      formatPickup: (value) => this.formatSignedCurrency(value),
    };
  }

  // ADR only has an STLY pickup for yesterday, like its pickup.
  get adrStlyPickup(): number | undefined {
    return this.args.smartInsightsKpiComparisonPeriod ===
      SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY
      ? (this.kpis.adrStlyPickupVsYesterday ?? undefined)
      : undefined;
  }

  get adrValueFormatted(): string {
    return this.formatOrMissing(this.kpis.adr, (value) =>
      this.formatCurrency(value),
    );
  }

  get adrVsStly(): number | undefined {
    return this.differenceOrUndefined(this.kpis.adr, this.kpis.adrStly);
  }

  get adrVsStlyFormatted(): string | undefined {
    const vsStly = this.adrVsStly;

    return vsStly === undefined ? undefined : this.formatSignedCurrency(vsStly);
  }

  private get biRedirectId(): string {
    return this.args.smartInsightDay.meta?.biRedirectId ?? this.subscriptionId;
  }

  @cached
  get comparisonPeriodOptions(): ComparisonPeriodOption[] {
    return Object.values(SMART_INSIGHTS_KPI_COMPARISON_PERIODS).map(
      (period) => ({
        label: this.intl.t(
          SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS[period],
        ),
        value: period,
      }),
    );
  }

  get currency(): string | undefined {
    const { hotel, market } = this.hotelAndMarketService;

    if (market) {
      return market.revenueInsightCurrency ?? market.currencyCode;
    }

    return hotel?.revenueInsightCurrency ?? hotel?.currencyCode;
  }

  @cached
  get demandChange(): PickupChange {
    return this.pickupChange(
      this.kpis.demandPickupVsYesterday,
      this.kpis.demandPickupVs3DaysAgo,
      this.kpis.demandPickupVs7DaysAgo,
      (value) =>
        this.intl.formatNumber(value, {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          signDisplay: 'always',
        }),
    );
  }

  get demandLevel(): MarketDemandLevel | null {
    return getDemandLevel(this.kpis.demand);
  }

  @cached
  get demandSectionSource(): SmartInsightDayInsightEntrySource | null {
    const sources = [
      this.buildMarketInsightSource(),
      this.buildRateInsightSource(),
      this.buildOverviewSource(OD_SELECTED_TILES_IDS.demand_level),
    ];

    return sources.find((s) => s.hasAccess) ?? null;
  }

  get demandValueFormatted(): string {
    return this.formatOrMissing(this.kpis.demand, (value) =>
      this.intl.formatNumber(value, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'never',
      }),
    );
  }

  get isAdrMissing(): boolean {
    return this.kpis.adr === null || this.kpis.adr === undefined;
  }

  get isOccupancyMissing(): boolean {
    return this.kpis.occupancy === null || this.kpis.occupancy === undefined;
  }

  get isOwnRateSoldOut(): boolean {
    return this.kpis.message === 'rates.soldout';
  }

  get isRoomNightsMissing(): boolean {
    return this.kpis.roomNights === null || this.kpis.roomNights === undefined;
  }

  private get kpis(): SmartInsightDay['kpis'] {
    return this.args.smartInsightDay.kpis;
  }

  @cached
  get medianCompsetRateChange(): PickupChange {
    return this.pickupChange(
      this.kpis.medianCompsetRatePickupVsYesterday,
      this.kpis.medianCompsetRatePickupVs3DaysAgo,
      this.kpis.medianCompsetRatePickupVs7DaysAgo,
      (value) => this.formatSignedCurrency(value),
    );
  }

  get medianCompsetRateFormatted(): string {
    return this.formatOrMissing(this.kpis.medianCompsetRate, (value) =>
      this.formatCurrency(value),
    );
  }

  get occupancyChangeValue(): number {
    return this.byPeriod(
      this.kpis.occupancyPickupVsYesterday,
      this.kpis.occupancyPickupVs3DaysAgo,
      this.kpis.occupancyPickupVs7DaysAgo,
    );
  }

  get occupancyChangeValueFormatted(): string {
    return this.formatPoints(this.occupancyChangeValue);
  }

  get occupancyHover(): KpiHoverData {
    return {
      thisYear: this.kpis.occupancy,
      stly: this.kpis.occupancyStly,
      lastYearFinal: this.kpis.occupancyLastYearFinal,
      pickupThisYear: this.occupancyChangeValue,
      pickupStly: this.occupancyStlyPickup,
      pickupPeriod: this.args.smartInsightsKpiComparisonPeriod,
      format: (value) => this.formatOccupancy(value),
      formatPickup: (value) => this.formatPoints(value),
    };
  }

  get occupancyStlyPickup(): number {
    return this.byPeriod(
      this.kpis.occupancyStlyPickupVsYesterday,
      this.kpis.occupancyStlyPickupVs3DaysAgo,
      this.kpis.occupancyStlyPickupVs7DaysAgo,
    );
  }

  get occupancyValueFormatted(): string {
    return this.formatOrMissing(this.kpis.occupancy, (value) =>
      this.formatOccupancy(value),
    );
  }

  get occupancyVsStly(): number | undefined {
    return this.differenceOrUndefined(
      this.kpis.occupancy,
      this.kpis.occupancyStly,
    );
  }

  get occupancyVsStlyFormatted(): string | undefined {
    const vsStly = this.occupancyVsStly;

    return vsStly === undefined ? undefined : this.formatPoints(vsStly);
  }

  @cached
  get otbSectionSource(): SmartInsightDayInsightEntrySource | null {
    const sources = [
      this.buildSpiderSource(),
      this.buildOverviewSource(OD_SELECTED_TILES_IDS.occupancy),
      this.buildBookingsSource(),
    ];

    return sources.find((s) => s.hasAccess) ?? null;
  }

  @cached
  get ownRateChange(): PickupChange {
    return this.pickupChange(
      this.kpis.ownRatePickupVsYesterday,
      this.kpis.ownRatePickupVs3DaysAgo,
      this.kpis.ownRatePickupVs7DaysAgo,
      (value) => this.formatSignedCurrency(value),
    );
  }

  get ownRateFormatted(): string {
    if (this.isOwnRateSoldOut) {
      return this.intl.t('rates.soldout');
    }

    return this.formatOrMissing(this.kpis.ownRate, (value) =>
      this.formatCurrency(value),
    );
  }

  get rateColumns(): RateColumn[] {
    return [
      {
        id: 'own-rate',
        alignClass: 'items-start',
        iconName: 'rates-lowest',
        label: this.intl.t('smart_insights.detail_view.rate_kpi.lowest_rate'),
        value: this.ownRateFormatted,
        change: this.ownRateChange,
      },
      {
        id: 'median-comp-rate',
        alignClass: 'items-end',
        label: this.intl.t(
          'smart_insights.detail_view.rate_kpi.median_comp_rate',
        ),
        value: this.medianCompsetRateFormatted,
        change: this.medianCompsetRateChange,
      },
    ];
  }

  private get rateiRedirectId(): string {
    return (
      this.args.smartInsightDay.meta?.rateiRedirectId ?? this.subscriptionId
    );
  }

  // The rates module is the only source that shows the lowest rate and the compset median.
  @cached
  get ratesSectionSource(): SmartInsightDayInsightEntrySource | null {
    const source = this.buildRateInsightSource();

    return source.hasAccess ? source : null;
  }

  get roomNightsChangeValue(): number {
    return this.byPeriod(
      this.kpis.roomNightsPickupVsYesterday,
      this.kpis.roomNightsPickupVs3DaysAgo,
      this.kpis.roomNightsPickupVs7DaysAgo,
    );
  }

  get roomNightsChangeValueFormatted(): string {
    return this.formatSignedInteger(this.roomNightsChangeValue);
  }

  get roomNightsHover(): KpiHoverData {
    return {
      thisYear: this.kpis.roomNights,
      stly: this.kpis.roomNightsStly,
      lastYearFinal: this.kpis.roomNightsLastYearFinal,
      hasRoomsLeftToSell: true,
      roomsLeftToSell: this.kpis.roomNightsRoomsLeftToSell,
      pickupThisYear: this.roomNightsChangeValue,
      pickupStly: this.roomNightsStlyPickup,
      pickupPeriod: this.args.smartInsightsKpiComparisonPeriod,
      format: (value) => this.formatInteger(value),
      formatPickup: (value) => this.formatSignedInteger(value),
    };
  }

  get roomNightsStlyPickup(): number {
    return this.byPeriod(
      this.kpis.roomNightsStlyPickupVsYesterday,
      this.kpis.roomNightsStlyPickupVs3DaysAgo,
      this.kpis.roomNightsStlyPickupVs7DaysAgo,
    );
  }

  get roomNightsValueFormatted(): string {
    return this.formatOrMissing(this.kpis.roomNights, (value) =>
      this.formatInteger(value),
    );
  }

  get roomNightsVsStly(): number | undefined {
    return this.differenceOrUndefined(
      this.kpis.roomNights,
      this.kpis.roomNightsStly,
    );
  }

  get roomNightsVsStlyFormatted(): string | undefined {
    const vsStly = this.roomNightsVsStly;

    return vsStly === undefined ? undefined : this.formatSignedInteger(vsStly);
  }

  get selectedComparisonPeriodOption(): ComparisonPeriodOption | undefined {
    return this.comparisonPeriodOptions.find(
      (option) => option.value === this.args.smartInsightsKpiComparisonPeriod,
    );
  }

  private get stayDate(): string {
    return this.args.smartInsightDay.stayDate;
  }

  private get subscriptionId(): string {
    return this.args.smartInsightDay.generationId?.split(',')[0] ?? '';
  }

  private buildBookingsSource(): SmartInsightDayInsightEntrySource {
    const queryParams = {
      reference: 'SWLY',
      fromDate: this.stayDate,
      toDate: this.stayDate,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.revenue-insight';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.bookings'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.revenue_insight'),
    };
  }

  private buildMarketInsightSource(): SmartInsightDayInsightEntrySource {
    const activeFilters = {
      [ACTIVE_FILTERS.DAY]: this.stayDate,
    };
    const queryParams = {
      activeFilters: stringifyObject(activeFilters),
      highlight: DETAIL_PANE_DETAIL_SECTION_CARD_IDS.DEMAND_INDICATORS,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.market-insight';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.market'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.market_insight'),
    };
  }

  private buildOverviewSource(
    highlightTileId: string,
  ): SmartInsightDayInsightEntrySource {
    const fromDate = dayjs(this.stayDate).startOf('month').format('YYYY-MM-DD');
    const toDate = dayjs(this.stayDate).endOf('month').format('YYYY-MM-DD');

    const queryParams = {
      activeFilters: stringifyObject({
        [ACTIVE_FILTERS.DAY]: this.stayDate,
      }),
      fromDate,
      toDate,
      rateParam: RATE_PARAMS.LOWEST,
      smartInsightsDay: this.stayDate,
      highlight: highlightTileId,
    };

    const routeName = 'hotel.overview';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.overview'),
      href,
      navigateToSource: (targetBlank = false): void => {
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('overview.view'),
    };
  }

  private buildRateInsightSource(): SmartInsightDayInsightEntrySource {
    const month = this.stayDate.slice(0, 7);
    const queryParams = {
      compsetId: '1',
      highlight: `smart-insight-day-${this.stayDate}`,
      month,
      rateParam: RATE_PARAMS.LOWEST,
      smartInsightsDay: this.stayDate,
      view: RATES_VIEWS.CALENDAR,
    };

    const routeName = 'hotel.rates';
    const href = this.router.urlFor(routeName, this.rateiRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.rates'),
      href,
      navigateToSource: (targetBlank = false): void => {
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.rateiRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.rates'),
    };
  }

  private buildSpiderSource(): SmartInsightDayInsightEntrySource {
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const startDate = dayjs(this.stayDate)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs(this.stayDate).endOf('month').format('YYYY-MM-DD');
    const queryParams = {
      pickupFrom: yesterday,
      startDate,
      endDate,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.day-by-day.spider-dashboard';
    const href = this.router.urlFor(
      routeName,
      this.biRedirectId,
      DAY_BY_DAY_PATHS[0],
      { queryParams },
    );

    return {
      productName: this.intl.t('menu.spider.day-by-day'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.spider.updateDateRange(startDate, endDate, yesterday);
          this.router.transitionTo(
            routeName,
            this.biRedirectId,
            DAY_BY_DAY_PATHS[0],
            { queryParams },
          );
        }
      },
      hasAccess: this.hasFeature('app.spider'),
    };
  }

  private byPeriod<T>(yesterday: T, threeDaysAgo: T, sevenDaysAgo: T): T {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.THREE_DAYS_AGO:
        return threeDaysAgo;
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.SEVEN_DAYS_AGO:
        return sevenDaysAgo;
      default:
        return yesterday;
    }
  }

  // The "vs. STLY" lines compare this year with last year, so a missing side
  // leaves the difference unknown.
  private differenceOrUndefined(
    thisYear: number | null | undefined,
    stly: number | null | undefined,
  ): number | undefined {
    return thisYear == null || stly == null ? undefined : thisYear - stly;
  }

  private formatCurrency(value: number): string {
    return this.intl.formatNumber(value, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  private formatInteger(value: number): string {
    return this.intl.formatNumber(value, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  private formatOccupancy(value: number): string {
    return this.intl.formatNumber(value / 100, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay: 'never',
    });
  }

  private formatOrMissing(
    value: Nullable,
    format: (value: number) => string,
  ): string {
    if (value === null || value === undefined) {
      return this.intl.t('general.missing');
    }

    return format(value);
  }

  private formatPoints(value: number): string {
    return this.formatSigned(value, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).concat('pt');
  }

  private formatSigned(value: number, options: NumberFormatOptions): string {
    return this.intl.formatNumber(value, {
      ...options,
      signDisplay: value === 0 ? 'never' : 'always',
    });
  }

  private formatSignedCurrency(value: number): string {
    return this.formatSigned(value, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  private formatSignedInteger(value: number): string {
    return this.formatSigned(value, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  private hasFeature(featureString: string): boolean {
    return (
      this.args.smartInsightDay.meta?.featureFlags?.includes(featureString) ??
      false
    );
  }

  private pickupChange(
    yesterday: Nullable,
    threeDaysAgo: Nullable,
    sevenDaysAgo: Nullable,
    format: (value: number) => string,
  ): PickupChange {
    const value = this.byPeriod(yesterday, threeDaysAgo, sevenDaysAgo) ?? null;

    return {
      value,
      formatted:
        value === null ? this.intl.t('general.missing') : format(value),
    };
  }

  @action handleSourceClick(
    source: SmartInsightDayInsightEntrySource,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    source.navigateToSource(
      this.args.openInNewTab || event.metaKey || event.ctrlKey,
    );
  }

  @action onChangeComparisonPeriod(
    selectedOption: ComparisonPeriodOption,
  ): void {
    this.args.setSmartInsightsKpiComparisonPeriod(selectedOption.value);
  }

  <template>
    <div
      class="ic-product-tour-57546189-smart-insights-kpis flex flex-col gap-200"
    >
      <div class="flex flex-row items-center justify-between gap-200">
        <h4 class="text-300--bold">
          {{t "smart_insights.detail_view.kpis"}}
        </h4>

        <div class="flex flex-row items-center gap-100">
          <PrismSelectInput
            @isSmall={{true}}
            @onChange={{this.onChangeComparisonPeriod}}
            @options={{this.comparisonPeriodOptions}}
            @value={{this.selectedComparisonPeriodOption}}
          />
        </div>
      </div>

      <GeneralSmartInsightsDetailViewSmartInsightCard class="gap-400 p-400">
        <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
          @changeValue={{this.occupancyChangeValue}}
          @changeValueFormatted={{this.occupancyChangeValueFormatted}}
          @hover={{this.occupancyHover}}
          @isChangeValueHidden={{this.isOccupancyMissing}}
          @label={{t "general.occupancy"}}
          @onSourceClick={{this.handleSourceClick}}
          @paceValue={{this.occupancyVsStly}}
          @paceValueFormatted={{this.occupancyVsStlyFormatted}}
          @source={{this.otbSectionSource}}
          @value={{this.occupancyValueFormatted}}
        />

        <div class={{styles.kpi-divider}} />

        <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
          @changeValue={{this.roomNightsChangeValue}}
          @changeValueFormatted={{this.roomNightsChangeValueFormatted}}
          @hover={{this.roomNightsHover}}
          @isChangeValueHidden={{this.isRoomNightsMissing}}
          @label={{t "general.room_nights"}}
          @onSourceClick={{this.handleSourceClick}}
          @paceValue={{this.roomNightsVsStly}}
          @paceValueFormatted={{this.roomNightsVsStlyFormatted}}
          @source={{this.otbSectionSource}}
          @value={{this.roomNightsValueFormatted}}
        />

        <div class={{styles.kpi-divider}} />

        <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
          @changeValue={{this.adrChangeValue}}
          @changeValueFormatted={{this.adrChangeValueFormatted}}
          @hover={{this.adrHover}}
          @isChangeValueHidden={{this.isAdrMissing}}
          @label={{t "revenue_insight.adr"}}
          @onSourceClick={{this.handleSourceClick}}
          @paceValue={{this.adrVsStly}}
          @paceValueFormatted={{this.adrVsStlyFormatted}}
          @source={{this.otbSectionSource}}
          @value={{this.adrValueFormatted}}
        />

        <div class={{styles.kpi-divider}} />

        <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
          @changeValue={{this.demandChange.value}}
          @changeValueFormatted={{this.demandChange.formatted}}
          @demandLevel={{this.demandLevel}}
          @isPaceValueHidden={{true}}
          @label={{t "demand.market_demand"}}
          @onSourceClick={{this.handleSourceClick}}
          @source={{this.demandSectionSource}}
          @value={{this.demandValueFormatted}}
        />

        <div class={{styles.kpi-divider}} />

        <div
          class="flex flex-col gap-200"
          data-test-id="smart-insight-rate-kpi"
        >
          {{#if this.ratesSectionSource}}
            <a
              class="{{titleStyles.title-link}} text-200--medium"
              data-test-id="smart-insight-rate-kpi-title"
              href={{this.ratesSectionSource.href}}
              {{on "click" (fn this.handleSourceClick this.ratesSectionSource)}}
            >{{t "menu.rates"}}</a>
          {{else}}
            <span
              class="{{titleStyles.title-text}} text-200--medium"
              data-test-id="smart-insight-rate-kpi-title"
            >{{t "menu.rates"}}</span>
          {{/if}}

          <div class="flex items-start justify-between gap-200">
            {{#each this.rateColumns as |column|}}
              <div class="flex flex-col {{column.alignClass}} gap-100">
                <div class="flex items-center gap-100">
                  {{#if column.iconName}}
                    <PrismIcon @name={{column.iconName}} @size="small" />
                  {{/if}}
                  <span
                    class="text-400--bold"
                    data-test-id={{concat "smart-insight-rate-kpi-" column.id}}
                  >{{column.value}}</span>

                  <GeneralSmartInsightsDetailViewSmartInsightKpiDelta
                    @formatted={{column.change.formatted}}
                    data-test-id={{concat
                      "smart-insight-rate-kpi-"
                      column.id
                      "-change"
                    }}
                  />
                </div>

                <div class="text-200--regular text-neutral-subdued">
                  {{column.label}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      </GeneralSmartInsightsDetailViewSmartInsightCard>
    </div>
  </template>
}
